import { useEffect, useRef, useState } from "react";
import { firebaseApp } from "./firebase.js";
import type * as FirestoreSDK from "firebase/firestore";

type LiveStatus = "connecting" | "live" | "offline" | "fallback";
let connection: Promise<{ sdk: typeof FirestoreSDK; db: FirestoreSDK.Firestore }> | null = null;
function connect() {
  connection ??= import("firebase/firestore").then((sdk) => {
    if (!firebaseApp) throw new Error("Firebase no configurado");
    const db = sdk.getFirestore(firebaseApp);
    if (import.meta.env.DEV && import.meta.env.VITE_FIRESTORE_EMULATOR_HOST === "127.0.0.1:8080") sdk.connectFirestoreEmulator(db, "127.0.0.1", 8080);
    return { sdk, db };
  });
  return connection;
}

export function useRealtimeRevision(onRefresh: () => void, ownerUid?: string) {
  const callback = useRef(onRefresh);
  const [status, setStatus] = useState<LiveStatus>(typeof window === "undefined" || navigator.onLine ? "connecting" : "offline");
  useEffect(() => { callback.current = onRefresh; }, [onRefresh]);
  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    let interval: ReturnType<typeof setInterval> | undefined;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    let pendingRefresh: ReturnType<typeof setTimeout> | undefined;
    let lastRevision: string | null = null;
    let connected = false;
    let listenerFailed = false;
    const refresh = () => {
      if (!active || !navigator.onLine || document.visibilityState !== "visible" || pendingRefresh) return;
      pendingRefresh = setTimeout(() => {
        pendingRefresh = undefined;
        if (active && navigator.onLine && document.visibilityState === "visible") callback.current();
      }, 180);
    };
    const fallback = () => {
      if (!active) return;
      connected = false;
      clearTimeout(watchdog); watchdog = undefined;
      setStatus(navigator.onLine ? "fallback" : "offline");
      interval ??= setInterval(refresh, 30000);
    };
    const connecting = () => {
      if (!navigator.onLine) { setStatus("offline"); return; }
      if (listenerFailed || interval) { fallback(); return; }
      setStatus("connecting");
      watchdog ??= setTimeout(fallback, 12000);
    };
    const offline = () => { connected = false; clearTimeout(watchdog); watchdog = undefined; setStatus("offline"); };
    const online = () => { if (connected) setStatus("live"); else connecting(); refresh(); };
    connecting();
    const subscribe = async () => {
      try {
        const { sdk, db } = await connect();
        if (!active) return;
        const reference = sdk.doc(db, ownerUid ? "merchant_revisions" : "catalog_revisions", ownerUid ?? "public");
        unsubscribe = sdk.onSnapshot(reference, { includeMetadataChanges: true }, (snapshot) => {
          if (!active) return;
          if (snapshot.metadata.fromCache) { connected = false; connecting(); return; }
          connected = true;
          clearTimeout(watchdog); watchdog = undefined;
          clearInterval(interval); interval = undefined;
          setStatus(navigator.onLine ? "live" : "offline");
          const revision = typeof snapshot.data()?.revision === "string" ? snapshot.data()?.revision as string : "empty";
          if (revision !== lastRevision) { lastRevision = revision; refresh(); }
        }, () => {
          if (!active) return;
          listenerFailed = true; fallback();
        });
      } catch {
        listenerFailed = true; fallback();
      }
    };
    void subscribe();
    window.addEventListener("online", online); window.addEventListener("offline", offline);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => { active = false; unsubscribe?.(); clearInterval(interval); clearTimeout(watchdog); clearTimeout(pendingRefresh); window.removeEventListener("online", online); window.removeEventListener("offline", offline); document.removeEventListener("visibilitychange", refresh); window.removeEventListener("focus", refresh); };
  }, [ownerUid]);
  return status;
}

export const liveStatusLabel: Record<LiveStatus, string> = { connecting: "Conectando actualizaciones…", live: "Actualizaciones en vivo", offline: "Sin conexión · datos sin actualizar", fallback: "Actualización automática cada 30 s" };
